import React from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { signInWithRedirect } from 'aws-amplify/auth';
import awsConfig from '../../aws-exports';

const authFormFields = {
    signIn: {
        username: { label: 'Username', placeholder: 'Enter your username', isRequired: true },
        password: { label: 'Password', placeholder: 'Enter your password', isRequired: true },
    },
};

// ── Google — manually triggered so we can place it anywhere in the UI ─────────
function GoogleSignInButton() {
    const handle = async () => {
        try { await signInWithRedirect({ provider: 'Google' }); }
        catch (err) { console.error('[Auth] Google sign-in error:', err); }
    };
    return (
        <button type="button" id="google-sso-btn" className="sym-sso-btn" onClick={handle}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Google
        </button>
    );
}

// ── Microsoft — Amplify has no built-in provider; we call signInWithRedirect ──
function MicrosoftSignInButton() {
    const handle = async () => {
        try {
            await signInWithRedirect({ provider: { custom: awsConfig.microsoftCognitoProviderName as string } });
        } catch (err) { console.error('[Auth] Microsoft sign-in error:', err); }
    };
    return (
        <button type="button" id="microsoft-sso-btn" className="sym-sso-btn" onClick={handle}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="20" height="20" aria-hidden="true">
                <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                <path fill="#f35325" d="M1 1h10v10H1z" />
                <path fill="#81bc06" d="M12 1h10v10H12z" />
                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                <path fill="#ffba08" d="M12 12h10v10H12z" />
            </svg>
            Microsoft
        </button>
    );
}

// ── Amplify custom UI slots ───────────────────────────────────────────────────
const authComponents = {
    SignIn: {
        Header() {
            return (
                <div className="sym-card-header">
                    <h1 className="sym-card-title">Welcome back</h1>
                    <p className="sym-card-sub">
                        Sign in to your <strong>Symbian Health</strong> account
                    </p>
                    {/* ── SSO: shared label + side-by-side provider buttons ── */}
                    <div className="sym-sso-group">
                        <span className="sym-sso-label">Continue with</span>
                        <div className="sym-sso-row">
                            <GoogleSignInButton />
                            <MicrosoftSignInButton />
                        </div>
                    </div>
                    {/* ── Divider before the credentials form ── */}
                    <div className="sym-sso-divider">
                        <span>or continue with email</span>
                    </div>
                </div>
            );
        },
        Footer() {
            return (
                <div className="sym-card-foot">
                    <div className="sym-card-foot-links">
                        <span>Need help?</span>
                        <a href="mailto:support@symbian.health">Contact Support</a>
                    </div>
                    <p className="sym-card-copy">
                        &copy; {new Date().getFullYear()} Symbian Health &middot; HIPAA Compliant
                    </p>
                </div>
            );
        },
    },
};

// ── Left branding panel ───────────────────────────────────────────────────────
function AuthLeftPanel() {
    return (
        <div className="sym-panel">
            {/* ── Background layers ── */}
            <div className="sym-panel-bg" aria-hidden="true">
                <div className="sym-orb sym-orb-1" />
                <div className="sym-orb sym-orb-2" />
                <div className="sym-orb sym-orb-3" />
                <div className="sym-orb sym-orb-4" />
                <div className="sym-grid" />
                <div className="sym-scan-line" />
            </div>

            <div className="sym-panel-inner">

                {/* ── Logo header ── */}
                <header className="sym-panel-head">
                    <img src="/assets/svgs/symbian-health.svg" alt="Symbian Health" className="sym-panel-logo-img" />
                </header>

                {/* ── Hero section ── */}
                <section className="sym-panel-hero">
                    <div className="sym-hero-eyebrow">
                        <span className="sym-eyebrow-dot" />
                        <span>Radiology Intelligence Platform</span>
                    </div>

                    <div className="sym-headline-wrap">
                        <div className="sym-headline-glow" aria-hidden="true" />
                        <h1 className="sym-panel-headline">
                            Intelligent{' '}
                            <span className="sym-panel-headline-accent">Radiology</span>
                            <br />Workspace
                        </h1>
                    </div>

                    <p className="sym-panel-desc">
                        Precision imaging, clinical documentation,
                        and AI&#8209;assisted reporting — unified in one
                        enterprise&#8209;grade platform.
                    </p>

                    <ul className="sym-features" aria-label="Key features">
                        <li className="sym-feature-item">
                            <span className="sym-feature-icon" aria-hidden="true">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(56,189,248,0.45)" strokeWidth="1.5" />
                                    <path d="M8 12l3 3 5-5" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span>AI&#8209;powered diagnostic reporting</span>
                        </li>
                        <li className="sym-feature-item">
                            <span className="sym-feature-icon" aria-hidden="true">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(56,189,248,0.45)" strokeWidth="1.5" />
                                    <path d="M8 12l3 3 5-5" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span>Real&#8209;time multi&#8209;site collaboration</span>
                        </li>
                        <li className="sym-feature-item">
                            <span className="sym-feature-icon" aria-hidden="true">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(56,189,248,0.45)" strokeWidth="1.5" />
                                    <path d="M8 12l3 3 5-5" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span>HIPAA&#8209;compliant zero&#8209;trust security</span>
                        </li>
                    </ul>
                </section>

                {/* ── EKG pulse ── */}
                <div className="sym-pulse-line" aria-hidden="true">
                    <svg viewBox="0 0 600 60" preserveAspectRatio="none" className="sym-pulse-svg">
                        <polyline
                            points="0,30 60,30 80,30 100,8 115,52 130,18 145,36 160,30 240,30 260,30 280,8 295,52 310,18 325,36 340,30 420,30 440,30 460,8 475,52 490,18 505,36 520,30 600,30"
                            fill="none" stroke="url(#pulseGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        />
                        <defs>
                            <linearGradient id="pulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(56,189,248,0)" />
                                <stop offset="20%" stopColor="rgba(56,189,248,0.55)" />
                                <stop offset="50%" stopColor="rgba(147,197,253,0.95)" />
                                <stop offset="80%" stopColor="rgba(56,189,248,0.55)" />
                                <stop offset="100%" stopColor="rgba(56,189,248,0)" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                {/* ── Stats strip ── */}
                <div className="sym-metrics">
                    <div className="sym-metric">
                        <span className="sym-metric-value">2.4M+</span>
                        <span className="sym-metric-label">Studies Processed</span>
                    </div>
                    <div className="sym-metric-divider" />
                    <div className="sym-metric">
                        <span className="sym-metric-value">99.97%</span>
                        <span className="sym-metric-label">Uptime SLA</span>
                    </div>
                    <div className="sym-metric-divider" />
                    <div className="sym-metric">
                        <span className="sym-metric-value">&lt;0.8s</span>
                        <span className="sym-metric-label">Avg Load Time</span>
                    </div>
                </div>

                {/* ── Bottom: compliance + trust ── */}
                <div className="sym-bottom">
                    <div className="sym-bottom-sep" aria-hidden="true" />
                    <div className="sym-badges">
                        <div className="sym-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                            <span>HIPAA</span>
                        </div>
                        <div className="sym-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                            <span>SOC 2 Type II</span>
                        </div>
                        <div className="sym-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                            <span>DICOM 3.0</span>
                        </div>
                        <div className="sym-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                            <span>HL7 FHIR</span>
                        </div>
                    </div>
                    <footer className="sym-panel-trust">
                        <div className="sym-trust-left">
                            <span className="sym-trust-dot" aria-hidden="true" />
                            <span>Enterprise-grade security</span>
                        </div>
                        <span>End-to-end encrypted</span>
                    </footer>
                </div>

            </div>
        </div>
    );
}

// ── Splash Screen ─────────────────────────────────────────────────────────────
function SplashScreen() {
    return (
        <div className="sym-splash">
            <img
                src="/assets/svgs/symbian-logo.svg"
                alt="Symbian Health"
                className='h-10 sm:h-12 md:h-14 lg:h-16 w-auto'
            />
        </div>
    );
}

// ── Auth shell ────────────────────────────────────────────────────────────────
function AuthShell({ appRoutes }: { appRoutes: React.ReactNode }) {
    const { authStatus } = useAuthenticator(ctx => [ctx.authStatus]);

    if (authStatus === 'configuring') return <SplashScreen />;
    if (authStatus === 'authenticated') return <>{appRoutes}</>;
    return (
        <div className="sym-shell">
            <AuthLeftPanel />
            <div className="sym-card-wrap">
                <div className="sym-card-scene">
                    <Authenticator
                        hideSignUp
                        formFields={authFormFields}
                        components={authComponents}
                    />
                </div>
            </div>
        </div>
    );
}

interface CustomAuthenticatorProps { appRoutes: React.ReactNode; }

export default function CustomAuthenticator({ appRoutes }: CustomAuthenticatorProps) {
    return (
        <Authenticator.Provider>
            <AuthShell appRoutes={appRoutes} />
        </Authenticator.Provider>
    );
}
